import { useEffect, useRef, useState } from 'react';

import Panel from '../components/Panel.jsx';

/**
 * @file Bottom-right perf readout. Draw calls/triangles/memory come straight
 * from `renderer.info`, polled at 2Hz (that object is mutated in place by three
 * every frame, so it is read on an interval rather than subscribed to).
 *
 * FPS counts the engine's `render` bus event over a rolling ~500ms window. It
 * used to count a private rAF loop, and that was WRONG the moment the frame cap
 * landed: rAF fires at DISPLAY rate whether or not the engine drew, so a capped
 * 30 still read "60 fps · cap 30". Only the `render` event means a frame was
 * actually submitted. (`render` is per-frame — it is the throttled `hud:*`
 * events that would under-sample, not this one.)
 *
 * The `cap N` tag shows the automatic render cap (see `core/perfGovernor.js`) —
 * without it a locked 30 on a weak GPU looks like the badge is broken.
 */
export default function PerfBadge({ engine }) {
  const [stats, setStats] = useState({ fps: 0, calls: 0, triangles: 0, geometries: 0, textures: 0, programs: 0 });
  const [expanded, setExpanded] = useState(false);
  const [frameCap, setFrameCap] = useState(0);

  const frameCountRef = useRef(0);
  const windowStartRef = useRef(0);
  const fpsRef = useRef(0);

  useEffect(() => {
    if (!engine) return undefined;

    frameCountRef.current = 0;
    windowStartRef.current = performance.now();
    fpsRef.current = 0;

    const offRender = engine.bus?.on?.('render', () => {
      frameCountRef.current += 1;
      const now = performance.now();
      const elapsed = now - windowStartRef.current;
      if (elapsed >= 500) {
        fpsRef.current = (frameCountRef.current / elapsed) * 1000;
        frameCountRef.current = 0;
        windowStartRef.current = now;
      }
    });

    const interval = setInterval(() => {
      const info = engine.renderer?.info;
      setStats({
        fps: fpsRef.current,
        calls: info?.render?.drawCalls ?? info?.render?.calls ?? 0,
        triangles: info?.render?.triangles ?? 0,
        geometries: info?.memory?.geometries ?? 0,
        textures: info?.memory?.textures ?? 0,
        programs: info?.memory?.programs ?? 0,
      });
    }, 500);

    return () => {
      offRender?.();
      clearInterval(interval);
    };
  }, [engine]);

  // The cap can engage before this component mounts (or before a remount), so
  // read the live value as well as subscribing — an event-only badge lies.
  useEffect(() => {
    if (!engine) return undefined;
    setFrameCap(engine.loop?.frameCap?.() ?? 0);
    return engine.bus?.on?.('perf:framecap', ({ hz }) => setFrameCap(hz || 0));
  }, [engine]);

  const backend = engine?.caps?.webgpu ? 'WebGPU' : engine?.caps ? 'WebGL2' : '—';

  return (
    <Panel
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => setExpanded((v) => !v)}
      className="pointer-events-auto absolute bottom-3 right-3 cursor-pointer select-none px-2.5 py-1.5 font-mono text-[10px] leading-4 text-hud-dim transition hover:border-gold/60"
    >
      <div className="text-hud">
        {stats.fps.toFixed(0)} fps · {backend}
        {frameCap > 0 && <span className="text-gold"> · cap {frameCap.toFixed(0)}</span>}
      </div>
      <div>
        {stats.calls} calls · {formatCount(stats.triangles)} tris
      </div>
      {expanded && (
        <div className="mt-1 border-t border-hairline pt-1">
          <div>
            geo {stats.geometries} · tex {stats.textures} · prog {stats.programs}
          </div>
        </div>
      )}
    </Panel>
  );
}

function formatCount(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(Math.round(n));
}
